import { useState, useEffect } from 'react';
import { PackageOpen, FileSpreadsheet, Check, Clock, ChevronDown, ChevronUp, ChevronRight, Package, Trash2, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';
import { useRefresh } from '../components/Layout';

export const Pallets = ({ safeMode = false }) => {
    const { refreshStats } = useRefresh();
    const [pallets, setPallets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedPallet, setExpandedPallet] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState({}); // Track expanded categories
    const [palletCategories, setPalletCategories] = useState({});
    const [loadingCategories, setLoadingCategories] = useState({});
    const [viewMode, setViewMode] = useState('categories'); // 'categories' or 'products'

    useEffect(() => {
        loadPallets();
    }, []);

    const loadPallets = async () => {
        try {
            setLoading(true);
            const response = await api.get('/pallets');
            setPallets(response.data);
        } catch (err) {
            toast.error('Eroare la încărcarea paleților');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadPalletCategories = async (palletId, forceReload = false) => {
        if (palletCategories[palletId] && !forceReload) {
            return;
        }
        
        setLoadingCategories(prev => ({ ...prev, [palletId]: true }));
        try {
            const response = await api.get(`/pallets/${palletId}/categories`);
            setPalletCategories(prev => ({ ...prev, [palletId]: response.data }));
        } catch (err) {
            toast.error('Eroare la încărcarea categoriilor');
            console.error(err);
        } finally {
            setLoadingCategories(prev => ({ ...prev, [palletId]: false }));
        }
    };

    const togglePallet = async (palletId) => {
        if (expandedPallet === palletId) {
            setExpandedPallet(null);
        } else {
            setExpandedPallet(palletId);
            await loadPalletCategories(palletId);
        }
    };

    const toggleCategory = (palletId, categoryName) => {
        const key = `${palletId}-${categoryName}`;
        setExpandedCategories(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const downloadPalletReport = async (palletId, palletNumber) => {
        try {
            const response = await api.get(`/pallets/${palletId}/export`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `palet_${palletNumber}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Raport descărcat');
        } catch (err) {
            toast.error('Eroare la descărcare');
            console.error(err);
        }
    };

    const downloadPalletReportFull = async (palletId, palletNumber) => {
        try {
            const response = await api.get(`/pallets/${palletId}/export-full`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `palet_${palletNumber}_complet_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Raport complet descărcat');
        } catch (err) {
            toast.error('Eroare la descărcare');
            console.error(err);
        }
    };

    const deleteAllMovements = async (palletId) => {
        if (!window.confirm('Sigur vrei să ștergi TOATE mișcările din acest palet? Această acțiune nu poate fi anulată.')) return;
        
        try {
            await api.delete(`/pallets/${palletId}/movements`);
            toast.success('Toate mișcările au fost șterse');
            setPalletCategories(prev => ({ ...prev, [palletId]: [] }));
            refreshStats();
        } catch (err) {
            toast.error('Eroare la ștergere');
            console.error(err);
        }
    };

    const deleteProductMovement = async (palletId, productId, productName) => {
        if (!window.confirm(`Sigur vrei să ștergi mișcările pentru "${productName}" din acest palet?`)) return;
        
        try {
            await api.delete(`/pallets/${palletId}/movement/${productId}`);
            toast.success(`Mișcările pentru "${productName}" au fost șterse`);
            // Reload categories
            const response = await api.get(`/pallets/${palletId}/categories`);
            setPalletCategories(prev => ({ ...prev, [palletId]: response.data }));
            refreshStats();
        } catch (err) {
            toast.error('Eroare la ștergere');
            console.error(err);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('ro-RO', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateStr;
        }
    };

    return (
        <div data-testid="pallets-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Paleți Intrare
                    </h1>
                    <p className="text-zinc-500 mt-1">
                        Istoric și rapoarte paleți
                    </p>
                </div>
            </div>

            {/* Pallets List */}
            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-4">
                            <Skeleton className="h-6 w-1/3 mb-2" />
                            <Skeleton className="h-4 w-1/4" />
                        </div>
                    ))}
                </div>
            ) : pallets.length === 0 ? (
                <div className="empty-state bg-white border border-zinc-200 rounded-lg">
                    <PackageOpen size={64} className="text-zinc-300" />
                    <h3 className="text-lg font-medium text-zinc-900 mt-4">
                        Niciun palet
                    </h3>
                    <p className="text-zinc-500 mt-1">
                        Deschide un palet din Dashboard pentru a începe
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {pallets.map((pallet) => {
                        const isExpanded = expandedPallet === pallet.id;
                        const categories = palletCategories[pallet.id] || [];
                        const isLoadingCats = loadingCategories[pallet.id];
                        const isOpen = pallet.status === 'open';
                        
                        // Calculate totals
                        const totalQty = categories.reduce((sum, cat) => sum + cat.total_qty, 0);
                        const totalValue = categories.reduce((sum, cat) => sum + cat.total_value, 0);
                        
                        return (
                            <div
                                key={pallet.id}
                                className={`bg-white border rounded-lg overflow-hidden ${
                                    isOpen ? 'border-amber-300' : 'border-zinc-200'
                                }`}
                            >
                                {/* Pallet Header */}
                                <div
                                    className={`p-4 cursor-pointer ${isOpen ? 'bg-amber-50' : ''}`}
                                    onClick={() => togglePallet(pallet.id)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${isOpen ? 'bg-amber-100' : 'bg-zinc-100'}`}>
                                                <PackageOpen size={24} className={isOpen ? 'text-amber-600' : 'text-zinc-500'} />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                        Palet {pallet.number}
                                                    </h3>
                                                    {isOpen ? (
                                                        <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                                                            <Clock size={12} />
                                                            ACTIV
                                                        </span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 text-xs bg-zinc-100 text-zinc-600 rounded-full flex items-center gap-1">
                                                            <Check size={12} />
                                                            ÎNCHIS
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-zinc-500 mt-1">
                                                    Deschis: {formatDate(pallet.opened_at)}
                                                    {pallet.closed_at && (
                                                        <span> • Închis: {formatDate(pallet.closed_at)}</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            {!safeMode && (
                                                <Button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteAllMovements(pallet.id);
                                                    }}
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                                    title="Șterge toate mișcările"
                                                >
                                                    <Trash2 size={18} />
                                                </Button>
                                            )}
                                            <Button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    downloadPalletReport(pallet.id, pallet.number);
                                                }}
                                                variant="outline"
                                                size="sm"
                                                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                                title="Export categorii"
                                            >
                                                <FileSpreadsheet size={18} className="mr-1" />
                                                Categorii
                                            </Button>
                                            <Button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    downloadPalletReportFull(pallet.id, pallet.number);
                                                }}
                                                variant="outline"
                                                size="sm"
                                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                                title="Export complet"
                                            >
                                                <FileSpreadsheet size={18} className="mr-1" />
                                                Complet
                                            </Button>
                                            {isExpanded ? (
                                                <ChevronUp size={20} className="text-zinc-400" />
                                            ) : (
                                                <ChevronDown size={20} className="text-zinc-400" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Expanded Categories View */}
                                {isExpanded && (
                                    <div className="border-t border-zinc-200 p-4 bg-zinc-50">
                                        {isLoadingCats ? (
                                            <div className="space-y-2">
                                                <Skeleton className="h-4 w-full" />
                                                <Skeleton className="h-4 w-3/4" />
                                            </div>
                                        ) : categories.length === 0 ? (
                                            <p className="text-zinc-500 text-center py-4">
                                                Nicio mișcare înregistrată pe acest palet
                                            </p>
                                        ) : (
                                            <div className="space-y-3">
                                                {/* Summary */}
                                                <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-200">
                                                    <span className="font-medium text-zinc-700">Total Palet</span>
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-bold text-amber-600">{totalValue.toFixed(2)} lei</span>
                                                        <span className="text-xs text-zinc-400">({totalQty} buc)</span>
                                                    </div>
                                                </div>
                                                
                                                {/* Categories - Collapsible */}
                                                {categories.map((cat) => {
                                                    const catKey = `${pallet.id}-${cat.category}`;
                                                    const isCatExpanded = expandedCategories[catKey];
                                                    
                                                    return (
                                                        <div key={cat.category} className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
                                                            <button
                                                                onClick={() => toggleCategory(pallet.id, cat.category)}
                                                                className="w-full flex items-center justify-between p-2 bg-zinc-50 hover:bg-zinc-100 transition-colors"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    {isCatExpanded ? (
                                                                        <ChevronDown size={16} className="text-zinc-400" />
                                                                    ) : (
                                                                        <ChevronRight size={16} className="text-zinc-400" />
                                                                    )}
                                                                    <Tag size={14} className="text-violet-500" />
                                                                    <span className="font-medium text-sm text-zinc-800">{cat.category}</span>
                                                                    <span className="text-xs text-zinc-400">({cat.products.length})</span>
                                                                </div>
                                                                <div className="flex items-center gap-3 text-xs">
                                                                    <span className="text-zinc-700 font-bold">{cat.total_value.toFixed(2)} lei</span>
                                                                    <span className="text-zinc-400">({cat.total_qty} buc)</span>
                                                                </div>
                                                            </button>
                                                            
                                                            {isCatExpanded && (
                                                                <div className="divide-y divide-zinc-100">
                                                                    {cat.products.map((product) => (
                                                                        <div
                                                                            key={product.id}
                                                                            className="flex items-center justify-between px-3 py-1.5 hover:bg-zinc-50"
                                                                        >
                                                                            <div className="flex items-center gap-2">
                                                                                <Package size={12} className="text-zinc-300" />
                                                                                <span className="text-xs text-zinc-700">{product.name}</span>
                                                                            </div>
                                                                            <div className="flex items-center gap-2 text-xs">
                                                                                <span className="text-zinc-700 font-medium">{(product.price * product.total_qty).toFixed(2)} lei</span>
                                                                                <span className="text-zinc-400">({product.total_qty} buc)</span>
                                                                                {!safeMode && (
                                                                                    <button
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            deleteProductMovement(pallet.id, product.id, product.name);
                                                                                        }}
                                                                                        className="p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                                                                        title="Șterge"
                                                                                    >
                                                                                        <Trash2 size={12} />
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Pallets;
